import type { LagoonMessageRow } from '../../common/Utils/premiumCareDisclaimer';
import { resolvePremiumCareDisclaimer } from '../../common/Utils/premiumCareDisclaimer';

export const STEP_COUNT = 4;

export type RadiusOption = {
  dnt_value: string;
  label: string;
};

export type DirectoryTypeOption = {
  dnt_value: 'people' | 'places';
  title: string;
  description: string;
};

export type ProviderFilterOption = {
  dnt_value: 'all' | 'premium' | 'greenDiamond';
  title: string;
  description: string;
};

export type GreenDiamondPlan = {
  planIdentifier: string;
  policyNumber?: string;
  title: string;
  description?: string;
  dnt_value?: ProviderFilterOption['dnt_value'];
};

export type PrintDirectoryLagoonConfig = {
  radiusOptions?: RadiusOption[];
  directoryTypeOptions?: DirectoryTypeOption[];
  providerFilterOptions?: ProviderFilterOption[];
  stepTitles?: Record<string, string>;
  stepSubtitles?: Record<string, string>;
  pdfContent?: {
    introText?: string[];
    memberServicesText?: string;
    resourceItems?: string[];
    premiumText?: string;
    premiumEmphasisText?: string;
    hospitalText?: string;
    tierText?: string;
    referralNoteText?: string;
    footerText?: string[];
  };
};

export type PrintDirectoryPdfContent = NonNullable<
  PrintDirectoryLagoonConfig['pdfContent']
>;

const stripPremiumDisclaimerHtml = (text: string): string =>
  text.replace(/<\/?p>/g, '').replace(/<\/?b>/g, '');

const getPremiumEmphasisText = (disclaimerContent: string): string =>
  /<b>(.*?)<\/b>/.exec(disclaimerContent)?.[1] ??
  "It's intended only as a guide and should not be the sole factor considered when selecting a physician. Designations have a risk of error and members should discuss designations with a physician before choosing one. If a member already has a physician, they should also consult with them for advice on selecting other physicians.";

export const getDefaultPrintDirectoryPdfContent = (
  lagoonData?: LagoonMessageRow[]
): Required<PrintDirectoryPdfContent> => {
  const premiumDisclaimerContent = resolvePremiumCareDisclaimer({
    locale: 'en',
    lagoonData,
  }).content;

  return {
    introText: [
      'Our network of providers and facilities is always evolving. Before you make an appointment, check that your provider or facility participates in your network. To find out, you can ask your provider or facility, or you can check on myuhc.com.',
    ],
    memberServicesText:
      "If you don't have access to the internet or want to check that a provider or facility is in network, call Member Services at the toll-free number on the back of your ID card.",
    resourceItems: [
      'Up-to-date list of network providers and facilities',
      'Resources to help you make choices about your care',
      'Provider credentials',
      'Maps and directions',
    ],
    premiumText: stripPremiumDisclaimerHtml(premiumDisclaimerContent),
    premiumEmphasisText: getPremiumEmphasisText(premiumDisclaimerContent),
    hospitalText:
      "Hospital Affiliation displayed on this page doesn't indicate the hospital is in-network for this plan. Please conduct a hospital search to confirm if the given hospital is in-network for this plan.",
    tierText:
      'Members in health plans that offer tiered benefits may pay less for services provided by Tier 1 providers.',
    referralNoteText:
      "Your office visit won't be covered by your health plan without your primary care provider (PCP) referral.",
    footerText: [
      'The choice of provider is yours. This site is for informational purposes only. It is not a substitute for medical, behavioral health, dental, vision, or health care advice. This directory does not recommend a particular provider or type of behavioral health, medical, dental, vision or health care. If you believe you are experiencing a medical emergency, please call 911.',
      "Before scheduling an appointment or receiving services, please check with the provider and your health plan to confirm that the provider is participating, that the service is covered, and the costs that you may be charged for the service. The directory is updated weekly. If you think any information in the directory is inaccurate, let us know by clicking on 'Report Incorrect Information' on that provider's page, or calling the number on your health plan member ID card.",
      "** If a provider's entry contains a termination date, the provider's agreement with UnitedHealthcare ends on this date, and the provider is scheduled to leave UnitedHealthcare's provider network. If UnitedHealthcare and the provider reach a new agreement, the provider will remain in-network and the term date will be removed.",
    ],
  };
};

export const RADIUS_OPTIONS: RadiusOption[] = [
  { dnt_value: '1', label: 'Within 1 mile' },
  { dnt_value: '2', label: 'Within 2 mile' },
  { dnt_value: '5', label: 'Within 5 miles' },
  { dnt_value: '10', label: 'Within 10 miles' },
  { dnt_value: '20', label: 'Within 20 miles' },
  { dnt_value: '30', label: 'Within 30 miles' },
  { dnt_value: '50', label: 'Within 50 miles' },
  { dnt_value: '100', label: 'Within 100 miles' },
];

export const DIRECTORY_TYPE_OPTIONS: DirectoryTypeOption[] = [
  {
    dnt_value: 'people',
    title: 'People Directory',
    description: 'Doctors and other care providers listed by specialty',
  },
  {
    dnt_value: 'places',
    title: 'Places Directory',
    description: 'Hospitals, clinics, labs, imaging centers, etc.',
  },
];

export const PROVIDER_FILTER_OPTIONS: ProviderFilterOption[] = [
  {
    dnt_value: 'all',
    title: 'All Providers',
    description: 'View the complete list of in-network providers',
  },
  {
    dnt_value: 'premium',
    title: 'Premium Care Physician',
    description: 'View providers who meet premium care standards',
  },
];

export const GREEN_DIAMOND_FILTER_OPTION: ProviderFilterOption = {
  dnt_value: 'greenDiamond',
  title: 'Green Diamond Provider',
  description: '',
};

export const STEP_TITLES: Record<number, string> = {
  1: 'Select a {{ coverage }} directory',
  2: '{{ coverage }} – People Directory',
  3: '{{ coverage }} – People Directory',
  4: 'Review Selections',
};

export const STEP_TITLES_PLACES: Record<number, string> = {
  3: '{{ coverage }} – Place Directory',
};

export const STEP_SUBTITLES: Record<number, string> = {
  1: 'Please specify which type of {{ coverage }} directory you would like to create. We will provide up to 1,000 results.',
  2: 'You have chosen to create a {{ coverage }} directory of people who provide health services. Select "All Providers" to view the entire list, or refine your selection further with one of the other options.',
  3: 'You have chosen to create a {{ coverage }} directory of people who provide health services. Enter a street address, city & state, 5 digit zip code, county, or parish.',
};

export const STEP_SUBTITLES_PLACES: Record<number, string> = {
  3: 'You have chosen to create a {{ coverage }} directory of places that provide health services. Enter a street address, city & state, 5 digit zip code, county, or parish.',
};

export const COVERAGE_LABELS: Record<string, string> = {
  M: 'Medical',
  B: 'Behavioral Health',
  D: 'Dental',
  V: 'Vision',
} as const;
